import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PhoneIphone as PhoneIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

// ============================================
// Style constants
// ============================================
const colors = {
  bg: '#111827',
  cardBg: '#1f2937',
  border: '#374151',
  primary: '#B25373',
  primaryHover: '#8E3D59',
  textWhite: '#ffffff',
  textGray300: '#d1d5db',
  textGray400: '#9ca3af',
  textGray500: '#6b7280',
  danger: '#ef4444',
  dangerLight: '#fca5a5',
  dangerBg: 'rgba(239, 68, 68, 0.1)',
};

// ============================================
// Login Component
// ============================================

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, error, isLoading, clearError } = useAuth();

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validation, setValidation] = useState({
    phone: true,
    password: true,
  });

  const validateForm = () => {
    const isValid = {
      phone: phone.trim() !== '',
      password: password.trim() !== '',
    };
    setValidation(isValid);
    return isValid.phone && isValid.password;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (validateForm()) {
      try {
        const response = await login(phone.trim(), password);

        if (response.status === 'success' && response.data?.requiresOTP) {
          // Persist the identifier (phone) the backend expects for the OTP step
          sessionStorage.setItem('auth_identifier', response.data.identifier || phone.trim());
          if (response.data.maskedPhone) {
            sessionStorage.setItem('auth_masked_phone', response.data.maskedPhone);
          }
          navigate('/verify-otp');
        } else if (response.status === 'success') {
          navigate('/');
        }
      } catch (err) {
        // Error handled by context
      }
    }
  };

  const inputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    paddingRight: 40,
    paddingLeft: 12,
    paddingTop: 10,
    paddingBottom: 10,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    backgroundColor: colors.cardBg,
    color: colors.textWhite,
    fontSize: 14,
    fontFamily: 'inherit',
    direction: 'ltr',
    textAlign: 'left',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const passwordInputStyle: React.CSSProperties = {
    ...inputStyle,
    paddingLeft: 40,
  };

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.bg,
        padding: '48px 16px',
      }}
    >
      <Box
        sx={{
          maxWidth: 448,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <ShieldIcon sx={{ fontSize: 40, color: '#fff' }} />
          </Box>
          <Typography
            sx={{
              fontSize: 30,
              fontWeight: 800,
              color: colors.textWhite,
              lineHeight: 1.3,
            }}
          >
            پنل مدیریت
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: 14,
              color: colors.textGray400,
            }}
          >
            برای دسترسی به داشبورد مدیریت وارد شوید
          </Typography>
        </Box>

        {/* Error Message */}
        {error && (
          <Box
            sx={{
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              backgroundColor: colors.dangerBg,
            }}
          >
            <WarningIcon sx={{ color: colors.danger, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 14, color: colors.dangerLight, lineHeight: 1.6 }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Login Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Phone number */}
            <Box>
              <Typography
                component="label"
                htmlFor="phone"
                sx={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 500,
                  color: colors.textGray300,
                  mb: 0.5,
                }}
              >
                شماره موبایل
              </Typography>
              <Box sx={{ position: 'relative', mt: 0.5 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    right: 0,
                    pr: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 20, color: colors.textGray500 }} />
                </Box>
                <input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="username"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="09123456789"
                  style={{
                    ...inputStyle,
                    borderColor: !validation.phone ? colors.danger : colors.border,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.primary;
                    e.target.style.boxShadow = '0 0 0 2px rgba(178, 83, 115, 0.3)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = !validation.phone ? colors.danger : colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </Box>
              {!validation.phone && (
                <Typography sx={{ mt: 0.5, fontSize: 13, color: colors.danger }}>
                  شماره موبایل را وارد کنید
                </Typography>
              )}
            </Box>

            {/* Password */}
            <Box>
              <Typography
                component="label"
                htmlFor="password"
                sx={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 500,
                  color: colors.textGray300,
                  mb: 0.5,
                }}
              >
                رمز عبور
              </Typography>
              <Box sx={{ position: 'relative', mt: 0.5 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    right: 0,
                    pr: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                >
                  <LockIcon sx={{ fontSize: 20, color: colors.textGray500 }} />
                </Box>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  style={{
                    ...passwordInputStyle,
                    borderColor: !validation.password ? colors.danger : colors.border,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.primary;
                    e.target.style.boxShadow = '0 0 0 2px rgba(178, 83, 115, 0.3)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = !validation.password ? colors.danger : colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 4,
                    transform: 'translateY(-50%)',
                    color: colors.textGray500,
                    '&:hover': { color: colors.textGray400, backgroundColor: 'transparent' },
                  }}
                >
                  {showPassword ? (
                    <VisibilityOff sx={{ fontSize: 20 }} />
                  ) : (
                    <Visibility sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </Box>
              {!validation.password && (
                <Typography sx={{ mt: 0.5, fontSize: 13, color: colors.danger }}>
                  رمز عبور را وارد کنید
                </Typography>
              )}
            </Box>
          </Box>

          {/* Forgot Password */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Link
              to="/forgot-password"
              style={{
                fontSize: 13,
                color: colors.textGray400,
                textDecoration: 'none',
              }}
            >
              رمز عبور خود را فراموش کرده‌اید؟
            </Link>
          </Box>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              padding: '10px 16px',
              border: 'none',
              borderRadius: 8,
              boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'inherit',
              color: colors.textWhite,
              backgroundColor: isLoading ? colors.primaryHover : colors.primary,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isLoading) (e.target as HTMLElement).style.backgroundColor = colors.primaryHover;
            }}
            onMouseLeave={(e) => {
              if (!isLoading) (e.target as HTMLElement).style.backgroundColor = colors.primary;
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ color: '#fff' }} />
                <span>در حال ورود...</span>
              </>
            ) : (
              'ورود به پنل مدیریت'
            )}
          </button>
        </Box>

        {/* Security Notice */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 12, color: colors.textGray500, lineHeight: 1.8 }}>
            این بخش امن مدیریت است.
          </Typography>
          <Typography sx={{ fontSize: 12, color: colors.textGray500, lineHeight: 1.8 }}>
            تمام فعالیت‌ها ثبت و نظارت می‌شوند.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
